import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  GetProjectByIdParams,
  GetProjectByIdData,
  ServerApiError,
  serverApi,
  ServerApiConfig,
} from 'api/server';
import { ReduxState } from 'store';

import {
  MergeRequestState,
  getInitialRequestState,
  getFetchedRequestState,
  getFetchingRequestState,
} from '../serverUtils';

export interface GetProjectByIdState
  extends MergeRequestState<GetProjectByIdData, ServerApiError> {}

const selector = {
  state: (state: ReduxState) => state.server.getProjectById,
  isFetching: (state: ReduxState) => state.server.getProjectById.isFetching,
  data: (state: ReduxState) => state.server.getProjectById.data,
  error: (state: ReduxState) => state.server.getProjectById.error,
};

const initialState: GetProjectByIdState = {
  ...getInitialRequestState(),
  error: null,
  data: null,
};

const SLICE_NAME = '@server/getProjectById';

const requestThunk = createAsyncThunk(
  `${SLICE_NAME}/request`,
  (
    {
      params,
      config,
    }: { params: GetProjectByIdParams; config?: ServerApiConfig },
    { rejectWithValue },
  ) => serverApi.getProjectById(params, config).catch(rejectWithValue),
  {
    condition: (payload, { getState }) =>
      !selector.isFetching(getState() as ReduxState),
  },
);

const { actions, reducer } = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(requestThunk.pending, (state) => {
      Object.assign(state, getFetchingRequestState());
    });
    builder.addCase(requestThunk.fulfilled, (state, action) => {
      Object.assign(state, getFetchedRequestState());
      state.data = action.payload;
      state.error = null;
    });
    builder.addCase(requestThunk.rejected, (state, action) => {
      Object.assign(state, initialState);
      state.error = action.payload as ServerApiError;
    });
  },
});

interface ServerGetProjectById {
  action: typeof actions;
  thunk: {
    request: typeof requestThunk;
  };
  reducer: typeof reducer;
  selector: typeof selector;
}

export const getProjectById: ServerGetProjectById = {
  action: actions,
  thunk: {
    request: requestThunk,
  },
  reducer,
  selector,
};
